/**
 * Utility functions for detecting URL types and handling CTA clicks
 */

export type UrlType = "youtube" | "demo" | "external" | "internal";

/**
 * True when `href` points at a typical binary/download file so SPA navigation must not handle it
 * (React Router `<Link>` would otherwise match `*` and render the 404 page while the URL changes).
 */
export const isStaticDownloadPath = (href: string): boolean => {
    let pathname = href;
    try {
        if (href.startsWith("http://") || href.startsWith("https://")) {
            pathname = new URL(href).pathname;
        } else {
            pathname = href.split("?")[0].split("#")[0];
        }
    } catch {
        pathname = href.split("?")[0].split("#")[0];
    }
    return /\.(pdf|zip|docx?|xlsx?|csv)$/i.test(pathname);
};

/**
 * Checks if a URL is a YouTube video URL
 * Supports various YouTube URL formats:
 * - youtube.com/watch?v=VIDEO_ID
 * - youtu.be/VIDEO_ID
 * - youtube.com/embed/VIDEO_ID
 */
export const isYouTubeUrl = (url: string): boolean => {
    if (!url) {
        return false;
    }

    const patterns = [/youtube\.com\/watch\?v=/i, /youtu\.be\//i, /youtube\.com\/embed\//i];

    return patterns.some(pattern => pattern.test(url));
};

const DEMO_HOST_PATTERN = /^cpq-immosoerf\.demo\.quatico\.dev$/i;

/**
 * Checks if a URL is the demo URL (root only, not e.g. /playground.html).
 * Supports: https://cpq-immosoerf.demo.quatico.dev/?animatedTour=true&_gl=1*11m9f1g*_gcl_au*Nzk0ODc5NzYxLjE3NTgwOTcwMjA. and /demo
 */
export const isDemoUrl = (url: string): boolean => {
    if (!url) {
        return false;
    }

    if (url === "/demo") {
        return true;
    }

    try {
        const parsed = new URL(url, "https://example.com");
        const isDemoHost = DEMO_HOST_PATTERN.test(parsed.hostname);
        const isRootPath = parsed.pathname === "" || parsed.pathname === "/";
        return isDemoHost && isRootPath;
    } catch {
        return false;
    }
};

/**
 * Determines the type of URL
 * @returns 'youtube' | 'demo' | 'external' | 'internal'
 */
export const getUrlType = (url: string): UrlType => {
    if (!url) {
        return "internal";
    }

    if (isYouTubeUrl(url)) {
        return "youtube";
    }

    if (isDemoUrl(url)) {
        return "demo";
    }

    // Check if it's an external URL (starts with http:// or https://)
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return "external";
    }

    // Internal link (relative path, hash, etc.)
    return "internal";
};
